import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { defaultHistory } from "./history";
import { ENUM_EVENT } from "./utils/eventEmitter";

// One literal import() per remote — REQUIRED to be a literal string, not
// built from a variable. @originjs/vite-plugin-federation's transform finds
// these by walking the AST for ImportExpression nodes whose `source` is a
// string literal containing "/" (see node_modules/@originjs/vite-plugin-
// federation/dist/index.js, the `node.source.value` checks) — a
// runtime-built specifier is invisible to that check, so the plugin never
// rewrites it into its federation-loading code and the browser just tries
// (and fails) to fetch a module literally named e.g. "dp/bootstrap". An
// earlier version of this file used a variable specifier + @vite-ignore to
// keep Vitest happy with federation disabled — that broke real loading
// instead. Vitest is now handled via a resolve.alias in vite.config.js
// (test-only, stubs each "<name>/bootstrap" specifier) rather than by
// hiding the specifier from Vite.
//
// Keyed by the SAME short alias SCAIFrontendParams already uses for the
// legacy window.render<name> convention (e.g. DPAI's `name: "dp"`), not each
// remote's own internal federation `name` — the host's `remotes` map in
// vite.config.js is a local alias and doesn't need to match the remote's
// self-identifying name. Grows by one entry each time a remote finishes its
// own migration turn.
const REMOTE_BOOTSTRAPS = {
  dp: () => import("dp/bootstrap"),
  edm: () => import("edm/bootstrap"),
  snop: () => import("snop/bootstrap"),
  snoe: () => import("snoe/bootstrap"),
  ds: () => import("ds/bootstrap"),
  admin: () => import("admin/bootstrap"),
  integration: () => import("integration/bootstrap"),
  tms: () => import("tms/bootstrap"),
  snp: () => import("snp/bootstrap"),
};

// Counterpart to MicroFrontend.jsx for remotes that have been converted to
// Module Federation. Each remote exposes a `./bootstrap` module with
// `{ mount(container, options), unmount(container) }` — mount/unmount take
// the actual DOM node (via ref) rather than a container id string, so unlike
// the legacy loader this never depends on window.render<name> existing.
const dispatchLoader = (loading) => {
  globalThis.dispatchEvent(new CustomEvent(ENUM_EVENT.LOADER, { detail: { loading } }));
};

const FederatedMicroFrontend = ({ name, id }) => {
  const [failed, setFailed] = useState(false);
  const containerRef = useRef(null);
  const unmountRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Captured once, synchronously — containerRef.current gets nulled by
    // React as soon as this div is removed, which can happen before the
    // async mount() below resolves. Everything in this effect (including its
    // cleanup) uses this captured value instead of re-reading the ref.
    const container = containerRef.current;
    const loadBootstrap = REMOTE_BOOTSTRAPS[name];

    dispatchLoader(true);

    if (!loadBootstrap) {
      setFailed(true);
      dispatchLoader(false);
      return undefined;
    }

    loadBootstrap()
      .then(async (mod) => {
        if (cancelled || !container) return;
        // The plugin's consumer-side runtime always wraps a remote whose
        // exposed module has no `default` export (every mf-bootstrap.jsx
        // here uses named `mount`/`unmount` exports) as
        // `{ default: <actual module>, __esModule: true }` — see the `SS()`
        // helper in the compiled bundle. So the real { mount, unmount } pair
        // lives one level deeper than the module we get back.
        const { mount, unmount } = mod.default ?? mod;
        await mount(container, { containerId: id, history: defaultHistory });
        if (cancelled) {
          // Navigation happened while mount() was still in flight — the
          // cleanup below already ran and found unmountRef empty, so this
          // instance would otherwise leak its React root, Redux store, and
          // window event listeners forever. Tear it down now instead.
          unmount?.(container);
          return;
        }
        unmountRef.current = unmount;
      })
      .catch((err) => {
        console.error(`[FederatedMicroFrontend] failed to load "${name}/bootstrap":`, err);
        if (!cancelled) {
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) dispatchLoader(false);
      });

    return () => {
      cancelled = true;
      unmountRef.current?.(container);
      unmountRef.current = null;
      dispatchLoader(false);
    };
  }, [name, id]);

  // Stop clicks inside the mounted remote from bubbling up to container-app
  // click handlers. Attached as a native DOM listener rather than a JSX onClick
  // so this container stays a plain, non-interactive element (no a11y role /
  // keyboard-handler requirements for what is purely a mount point).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const stopClick = (event) => event.stopPropagation();
    container.addEventListener("click", stopClick);
    return () => container.removeEventListener("click", stopClick);
  }, []);

  return (
    <>
      {failed && (
        <div className="alert alert-danger fade show">
          Requested module is not available right now, please try later!
        </div>
      )}
      <div
        ref={containerRef}
        id={`${name}-federated-container`}
      />
    </>
  );
};

FederatedMicroFrontend.propTypes = {
  name: PropTypes.string,
  id: PropTypes.string,
};

export default FederatedMicroFrontend;
